import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { NavbarIcon } from "../../atoms/navbar/Icon";
import { QuestionChoice } from "../../models/types";
import { StringCustomField } from "../../components/CustomFieldForm";
import { useEffect, useState } from "react";

type SingleSelectionFieldFieldProps = {
  value: string;
  onChange: (newValue: string) => void;
  required?: boolean;
  choices: QuestionChoice[];
  disabled?: boolean;
};

const SingleSelectionOption = (props: {
  onClick: () => void;
  label: string;
  selected: boolean;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  return (
    <Box
      border={`1px solid ${theme.palette.custom.primaryBorder}`}
      borderRadius="3px"
      onClick={props.disabled ? () => {} : () => props.onClick()}
      sx={{
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.palette.custom.secondaryBackground,
        },
        backgroundColor: props.selected
          ? theme.palette.custom.secondaryBackground
          : theme.palette.custom.primaryBackground,
      }}
      display="flex"
      flexDirection="row"
      alignItems="center"
    >
      <IconButton>
        <NavbarIcon
          variant={props.selected ? "radio-checked" : "radio-unchecked"}
          sx={{
            height: "18px",
            width: "18px",
            minHeight: "18px",
            minWidth: "18px",
          }}
        />
      </IconButton>
      <Typography
        fontWeight={props.selected ? 600 : 400}
        variant="h4"
        color={theme.palette.custom.secondaryTypography}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: props.label,
          }}
        />
      </Typography>
    </Box>
  );
};

export const SingleSelectionField = ({
  choices,
  onChange,
  value,
  required,
  disabled,
}: SingleSelectionFieldFieldProps) => {
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(
    value === "Other" ||
      (value !== null && value !== "" && !choices.some((choice) => choice.value === value))
  );
  const [otherValue, setOtherValue] = useState<string>(value ?? "");
  useEffect(() => {
    if (otherValue && isOtherSelected) {
      onChange(otherValue);
    }
  }, [otherValue]);
  return (
    <Box display="flex" flexDirection="column" width="100%" gap="10px">
      {choices.map((choice) => (
        <SingleSelectionOption
          onClick={() => {
            if (choice.value === "Other") {
              onChange("");
              setIsOtherSelected(true);
            } else {
              setIsOtherSelected(false);
              onChange(choice.value);
            }
          }}
          label={choice.label}
          selected={choice.value === "Other" ? isOtherSelected : value === choice.value}
          disabled={disabled}
        />
      ))}
      {isOtherSelected && (
        <>
          <Typography variant="body2">If you didn't find the option enter it manually</Typography>
          <StringCustomField
            required={required}
            value={otherValue}
            onChange={setOtherValue}
            error={false}
            errorMessage=""
            disabled={disabled}
          />
        </>
      )}
    </Box>
  );
};

export const SingleSelectionReadOnly = ({
  value,
  choices,
}: {
  value: string;
  choices: QuestionChoice[];
}) => {
  const selectedChoice = choices.find((choice) => choice.value === value);
  return selectedChoice ? (
    <Typography variant="body2">
      <div
        dangerouslySetInnerHTML={{
          __html: selectedChoice?.label,
        }}
      />
    </Typography>
  ) : null;
};
